// API para el portal del cliente (autoservicio).
// Cada función chequea en el backend (RLS o RPC) que el usuario es el titular.

import { supabase } from './supabase'

/** Trae todas las suscripciones donde el user logueado es titular o beneficiario. */
export async function getMisSuscripciones(userId) {
  const { data, error } = await supabase
    .from('clientes')
    .select(`
      id, alias, barrio, direccion, apto, dia_entrega, ventana_horaria, semana_mes,
      tipo_vivienda, lat, lng, estado, pausa_hasta, dia_cobro_mes, modelo_cobro,
      kit_actual, fecha_inicio, fecha_proximo_cobro, notas, titular_id, beneficiario_id,
      mp_card_last4, mp_card_brand, mp_preapproval_id, mp_preapproval_status,
      fuera_zona, entrega_responsable,
      barrio_obj:barrios!clientes_barrio_id_fkey(id, nombre, dias),
      titular:profiles!clientes_titular_fkey(id, nombre, phone, email),
      beneficiario:profiles!clientes_beneficiario_fkey(id, nombre, phone, email),
      kit:kits!clientes_kit_actual_fkey(codigo, nombre, tier, precio_susc, personas)
    `)
    .or(`titular_id.eq.${userId},beneficiario_id.eq.${userId}`)
    .order('created_at', { ascending: false })
  if (error) { console.error('getMisSuscripciones:', error); return [] }
  return data || []
}

/** Próxima entrega programada de una suscripción. */
export async function getProximaEntrega(suscripcionId) {
  const { data, error } = await supabase
    .from('entregas')
    .select('id, numero_orden, fecha_programada, ventana_horaria, estado, kit_codigo')
    .eq('cliente_id', suscripcionId)
    .in('estado', ['programada', 'en_ruta'])
    .order('fecha_programada', { ascending: true })
    .limit(1)
    .maybeSingle()
  if (error) return null
  return data
}

/** Próximo pago pendiente de una suscripción. */
export async function getProximoPago(suscripcionId) {
  const { data, error } = await supabase
    .from('pagos')
    .select('id, monto, vence, estado, metodo, mes')
    .eq('cliente_id', suscripcionId)
    .in('estado', ['pendiente', 'atrasado'])
    .order('vence', { ascending: true, nullsFirst: false })
    .limit(1)
    .maybeSingle()
  if (error) return null
  return data
}

/** Historial de pagos. */
export async function getHistorialPagos(suscripcionId, limit = 20) {
  const { data } = await supabase
    .from('pagos')
    .select('id, monto, vence, mes, estado, metodo, pagado_at, notas')
    .eq('cliente_id', suscripcionId)
    .order('vence', { ascending: false, nullsFirst: false })
    .limit(limit)
  return data || []
}

/** Historial de entregas. */
export async function getHistorialEntregas(suscripcionId, limit = 20) {
  const { data } = await supabase
    .from('entregas')
    .select('id, numero_orden, fecha_programada, ventana_horaria, estado, kit_codigo, ts_entregada')
    .eq('cliente_id', suscripcionId)
    .order('fecha_programada', { ascending: false })
    .limit(limit)
  return data || []
}

/** Resumen completo del dashboard de un cliente (1 RPC). Incluye:
 *  - Próxima entrega (real desde DB o calculada según día/semana del barrio)
 *  - Fecha de corte D-2 (con snap a viernes)
 *  - Próximo pago pendiente
 *  - Bonificación 3 meses (mes actual, monto próximo cobro, ahorro)
 *  - Vecinos activos en la cuadra
 */
export async function getDashboardResumen(suscripcionId) {
  const { data, error } = await supabase.rpc('cliente_dashboard_resumen', {
    p_cliente_id: suscripcionId,
  })
  if (error) { console.error('getDashboardResumen:', error); return null }
  const r = data?.[0]
  if (!r) return null
  return {
    proximaEntregaReal: r.proxima_entrega_real,
    proximaEntregaCalc: r.proxima_entrega_calc,
    proximaEntregaCorte: r.proxima_entrega_corte,
    proximoPago: r.proximo_pago_id ? {
      id: r.proximo_pago_id,
      monto: Number(r.proximo_pago_monto),
      vence: r.proximo_pago_vence,
      estado: r.proximo_pago_estado,
    } : null,
    bonif: {
      mesActual:       Number(r.bonif_mes_actual),
      mesesRestantes:  Number(r.bonif_meses_restantes),
      montoFull:       Number(r.bonif_monto_full),
      montoNormal:     Number(r.bonif_monto_normal),
      montoProximo:    Number(r.bonif_monto_proximo),
      ahorroTotal:     Number(r.bonif_ahorro_total),
      aplica:          r.bonif_aplica,
    },
    vecinos: {
      cuadra:        Number(r.vecinos_cuadra),
      paraDescuento: Number(r.vecinos_para_descuento),
    },
  }
}

/** Ahorro total acumulado del cliente vs supermercado (suscripción + pedidos one-shot). */
export async function getAhorroAcumulado(userId) {
  const { data, error } = await supabase.rpc('cliente_ahorro_acumulado', { p_user_id: userId })
  if (error) { console.error('getAhorroAcumulado:', error); return null }
  return data?.[0] || null
}

/* ──────────── ADMIN PEDIDOS ──────────── */

export async function adminGetPedidos({ estado = null, limit = 50 } = {}) {
  const { data, error } = await supabase.rpc('admin_get_pedidos', { p_estado: estado, p_limit: limit })
  if (error) { console.error('adminGetPedidos:', error); return [] }
  return data || []
}

export async function adminMarcarPedidoPagado(pedidoId, referencia = null) {
  const { data, error } = await supabase.rpc('admin_marcar_pedido_pagado', { p_pedido_id: pedidoId, p_referencia: referencia })
  if (error) throw error
  return data
}

export async function adminCancelarPedido(pedidoId, motivo = null) {
  const { data, error } = await supabase.rpc('admin_cancelar_pedido', { p_pedido_id: pedidoId, p_motivo: motivo })
  if (error) throw error
  return data
}

/* ──────────── ADMIN RECORDATORIOS WA ──────────── */

export async function adminGetRecordatorios(fecha = null) {
  const { data, error } = await supabase.rpc('admin_get_recordatorios', { p_fecha: fecha })
  if (error) { console.error('adminGetRecordatorios:', error); return [] }
  return data || []
}

export async function adminMarcarRecordatorioEnviado(fuente, id) {
  const { error } = await supabase.rpc('admin_marcar_recordatorio_enviado', { p_fuente: fuente, p_id: id })
  if (error) throw error
}

export async function adminMarcarTodosRecordatoriosEnviados(fecha = null) {
  const { data, error } = await supabase.rpc('admin_marcar_todos_recordatorios_enviados', { p_fecha: fecha })
  if (error) throw error
  return data
}

/* ──────────── ADMIN STOCK ──────────── */

export async function adminGetAlertasStock() {
  const { data, error } = await supabase.rpc('admin_alertas_stock')
  if (error) { console.error('adminGetAlertasStock:', error); return [] }
  return data || []
}

/* ──────────── ADMIN BARRIOS ──────────── */

export async function adminGetBarrios() {
  const { data, error } = await supabase.rpc('admin_get_barrios')
  if (error) { console.error('adminGetBarrios:', error); return [] }
  return data || []
}

export async function adminCrearBarrio({ nombre, dias, notas = null }) {
  const { data, error } = await supabase.rpc('admin_crear_barrio', {
    p_nombre: nombre, p_dias: dias, p_notas: notas,
  })
  if (error) throw error
  return data
}

export async function adminActualizarBarrio(id, { nombre, dias, notas = null, activo }) {
  const { data, error } = await supabase.rpc('admin_actualizar_barrio', {
    p_id: id, p_nombre: nombre, p_dias: dias, p_notas: notas, p_activo: activo,
  })
  if (error) throw error
  return data
}

/* ──────────── ACCIONES (RPCs) ──────────── */

export async function pausarSuscripcion(id, hasta = null) {
  const { error } = await supabase.rpc('cliente_pausar_suscripcion', {
    susc_id: id,
    hasta: hasta || null,
  })
  if (error) throw error
}

export async function reactivarSuscripcion(id) {
  const { error } = await supabase.rpc('cliente_reactivar_suscripcion', { susc_id: id })
  if (error) throw error
}

export async function cambiarDiaSuscripcion(id, dia, ventana) {
  const { error } = await supabase.rpc('cliente_cambiar_dia', {
    susc_id: id,
    dia_param: dia,
    ventana_param: ventana,
  })
  if (error) throw error
}

export async function cambiarKitSuscripcion(id, kitCodigo) {
  const { error } = await supabase.rpc('cliente_cambiar_kit', {
    susc_id: id,
    kit_codigo_param: kitCodigo,
  })
  if (error) throw error
}

export async function cancelarMiSuscripcion(id, motivo = null) {
  const { error } = await supabase.rpc('cliente_cancelar_suscripcion', {
    susc_id: id,
    motivo,
  })
  if (error) throw error
}

/** Lista de kits disponibles (para el comparador básico). */
export async function getKitsDisponibles() {
  const { data } = await supabase
    .from('kits')
    .select('codigo, nombre, tier, precio_susc, personas, descripcion, imagen_url, destacado')
    .order('precio_susc', { ascending: true })
  return data || []
}

/** Trae kits con sus productos incluidos (para el comparador visual).
 *  Usa una RPC SECURITY DEFINER porque la tabla `productos` tiene RLS restrictivo
 *  (solo admin/repartidor) — la RPC expone únicamente los campos públicos. */
export async function getKitsConProductos() {
  const { data, error } = await supabase.rpc('cliente_get_kits_con_productos')
  if (error) { console.error('getKitsConProductos:', error); return [] }
  return (data || []).map(k => ({
    ...k,
    productos: Array.isArray(k.productos) ? k.productos : [],
  }))
}

/** Datos para mostrar como pagar (cuenta bancaria). */
export async function getCuentaCobroPublica() {
  const { data } = await supabase
    .from('app_config')
    .select('value')
    .eq('key', 'cuenta_cobro')
    .maybeSingle()
  try { return JSON.parse(data?.value || '{}') } catch { return null }
}

/** Actualizar datos del propio perfil. */
export async function actualizarMiPerfil(campos) {
  const updates = {}
  if (campos.nombre !== undefined) updates.nombre = campos.nombre?.trim() || null
  if (campos.phone !== undefined) updates.phone = campos.phone?.trim() || null
  // No dejamos editar email/documento sin verificación → solo admin puede
  const { error } = await supabase
    .from('profiles')
    .update(updates)
    .eq('id', (await supabase.auth.getUser()).data.user.id)
  if (error) throw error
}

/** Cambiar contraseña. */
export async function cambiarMiPassword(nuevaPassword) {
  const { error } = await supabase.auth.updateUser({ password: nuevaPassword })
  if (error) throw error
}

/** Lista de barrios activos (para el wizard). */
export async function getBarriosActivos() {
  const { data, error } = await supabase
    .from('barrios')
    .select('id, nombre, dias')
    .eq('activo', true)
    .order('nombre')
  if (error) return []
  return data || []
}

/* ──────────── PEDIDOS (one-shot: kit unico, tienda) ──────────── */

/** Lista los productos vendibles para La Tienda (ordenados por categoria/tier/precio). */
export async function getProductosTienda() {
  const { data, error } = await supabase.rpc('tienda_get_productos')
  if (error) { console.error('getProductosTienda:', error); return [] }
  return data || []
}

/** Pedidos del cliente actual (one-shot, no suscripciones). */
export async function getMisPedidos(limit = 20) {
  const { data, error } = await supabase.rpc('cliente_get_mis_pedidos', { p_limit: limit })
  if (error) { console.error('getMisPedidos:', error); return [] }
  return data || []
}

/**
 * Crea un pedido one-shot (kit unico, productos sueltos o adicional de suscriptor).
 * El backend valida envio, calcula fecha_corte y crea el pedido.
 *
 * @param {Object} p
 * @param {'compra_unica'|'productos_sueltos'|'adicional_suscripcion'} p.tipo
 * @param {string|null} [p.kitCodigo]
 * @param {Array<{sku, cantidad, precio_unit, nombre, presentacion}>} [p.items]
 * @param {string} p.barrioId
 * @param {string} p.direccion
 * @param {string} [p.apto]
 * @param {number} [p.lat]
 * @param {number} [p.lng]
 * @param {string} p.diaSemana          'lunes' | 'martes' | etc
 * @param {'manana'|'tarde'} [p.ventana='manana']
 * @param {string} p.fechaEntrega       'YYYY-MM-DD'
 * @param {boolean} [p.fueraDeCorte=false]
 * @param {'transferencia'|'tarjeta_credito'|'tarjeta_debito'|'abitab'|'redpagos'|'mercadopago'} [p.metodoPago]
 * @param {string} [p.destinatarioNombre]   ej: "Para mi mamá"
 * @param {string} [p.destinatarioTelefono]
 * @param {string} [p.compradorNombre]
 * @param {string} [p.compradorTelefono]
 * @param {string} [p.compradorEmail]
 * @param {string} [p.notas]
 *
 * @returns {Promise<Object>} pedido completo con id, numero_orden, fecha_corte
 */
export async function crearPedido(p) {
  const { data, error } = await supabase.rpc('cliente_crear_pedido', {
    p_tipo:                  p.tipo,
    p_kit_codigo:            p.kitCodigo || null,
    p_items:                 p.items || [],
    p_barrio_id:             p.barrioId,
    p_direccion:             p.direccion,
    p_apto:                  p.apto || null,
    p_lat:                   p.lat || null,
    p_lng:                   p.lng || null,
    p_dia_semana:            p.diaSemana,
    p_ventana:               p.ventana || 'manana',
    p_fecha_entrega:         p.fechaEntrega,
    p_destinatario_nombre:   p.destinatarioNombre || null,
    p_destinatario_telefono: p.destinatarioTelefono || null,
    p_comprador_nombre:      p.compradorNombre || null,
    p_comprador_telefono:    p.compradorTelefono || null,
    p_comprador_email:       p.compradorEmail || null,
    p_metodo_pago:           p.metodoPago || null,
    p_fuera_de_corte:        !!p.fueraDeCorte,
    p_notas:                 p.notas || null,
    p_cliente_suscriptor_id: p.clienteSuscriptorId || null,
  })
  if (error) throw error
  return data
}

/** Crea una suscripción nueva como cliente self-service. */
export async function crearMiSuscripcion(params) {
  const { data, error } = await supabase.rpc('cliente_crear_mi_suscripcion', {
    alias_param:        params.alias || null,
    barrio_id_param:    params.barrioId,
    direccion_param:    params.direccion,
    apto_param:         params.apto || null,
    tipo_vivienda_param: params.tipoVivienda || null,
    kit_codigo_param:   params.kitCodigo || null,
    dia_entrega_param:  params.diaEntrega || null,
    ventana_param:      params.ventana || 'manana',
    semana_mes_param:   params.semanaMes || null,
    dia_cobro_mes_param: params.diaCobroMes || null,
    notas_param:        params.notas || null,
    lat_param:          params.lat || null,
    lng_param:          params.lng || null,
    primera_entrega_fecha_param:   params.primeraEntregaFecha || null,
    primera_entrega_recargo_param: Number(params.primeraEntregaRecargo || 0),
    primera_entrega_tipo_param:    params.primeraEntregaTipo || null,
  })
  if (error) throw error
  return data
}

/**
 * Devuelve las opciones para la PRIMERA entrega de una suscripcion.
 * Si esta dentro de corte: 1 opcion 'esperar' (gratis, dia natural).
 * Si esta fuera de corte: hasta 5 opciones (1 esperar + 1 saltar_corte + 3 fuera_ruta).
 *
 * @param {Object} p
 * @param {string} p.barrioId
 * @param {string} p.diaEntrega    'lunes' | 'martes' | ...
 * @param {number} p.semanaMes     1..4
 * @param {Date}   [p.desde]       default: ahora
 *
 * @returns {Promise<Array<{
 *   opcion: 'esperar'|'saltar_corte'|'fuera_ruta',
 *   fechaEntrega: string,         // 'YYYY-MM-DD'
 *   nombreDia: string,            // 'lunes' | 'martes' | ...
 *   fechaCorte: string,           // ISO timestamp
 *   recargo: number,              // 0 o tarifa fuera_ruta
 *   enRuta: boolean,
 *   esDiaElegido: boolean,
 * }>>}
 */
export async function simularOpcionesPrimeraEntrega({ barrioId, diaEntrega, semanaMes, desde }) {
  if (!barrioId || !diaEntrega || !semanaMes) return []
  const { data, error } = await supabase.rpc('simular_opciones_primera_entrega', {
    p_barrio_id:   barrioId,
    p_dia_entrega: diaEntrega,
    p_semana_mes:  Number(semanaMes),
    p_desde:       (desde || new Date()).toISOString(),
  })
  if (error) throw error
  return (data || []).map(r => ({
    opcion:       r.opcion,
    fechaEntrega: r.fecha_entrega,
    nombreDia:    r.nombre_dia,
    fechaCorte:   r.fecha_corte,
    recargo:      Number(r.recargo || 0),
    enRuta:       r.en_ruta,
    esDiaElegido: r.es_dia_elegido,
  }))
}
